import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { API, useAuth } from "@/App";
import { Flag, X, Send } from "lucide-react";
import { toast } from "sonner";

export default function GlobalReportButton() {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [page, setPage] = useState("");
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const token = localStorage.getItem("token");
  const location = useLocation();

  // Hide on doubt page (cramped bottom area with chat input)
  const isDoubtPage = location.pathname === "/doubt";
  if (!user || !token || isDoubtPage) return null;

  const submit = async () => {
    if (!description.trim()) { toast.error("Please describe the error"); return; }
    setSending(true);
    try {
      await fetch(`${API}/report-error`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ai_response: "",
          error_description: description,
          page: page || window.location.pathname,
          subject: "",
          topic: "",
        }),
      });
      toast.success("Error reported! Our team at aistuddybuddy3@gmail.com will review it.");
      setOpen(false);
      setDescription("");
      setPage("");
    } catch {
      toast.error("Failed to send report. Please try again.");
    }
    setSending(false);
  };

  return (
    <>
      {/* Floating icon button */}
      <button onClick={() => setOpen(true)} data-testid="global-report-btn"
        className="fixed bottom-28 right-4 z-[9998] flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg w-9 h-9 active:scale-95 sm:bottom-16"
        title="Report Error">
        <Flag className="w-4 h-4" />
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center bg-black/50 p-4" onClick={() => setOpen(false)}>
          <div className="bg-[#0F1B3D] border border-white/20 rounded-2xl shadow-2xl p-5 w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid="global-report-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-white text-base">Report an Error</h3>
              <button onClick={() => setOpen(false)} className="text-white/30 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-white/40 mb-3">Found a mistake or bug? Let us know so we can fix it. You can also email us at <a href="mailto:aistuddybuddy3@gmail.com" className="text-orange-400 hover:underline">aistuddybuddy3@gmail.com</a></p>
            
            <label className="text-[11px] text-white/50 font-semibold uppercase mb-1 block">Which page?</label>
            <select value={page} onChange={e => setPage(e.target.value)}
              className="w-full rounded-lg bg-white/5 border border-white/10 text-white text-sm h-9 px-3 mb-3 outline-none">
              <option value="" className="bg-[#0F1B3D]">Current page</option>
              <option value="chapter-explain" className="bg-[#0F1B3D]">Chapter Explanation</option>
              <option value="doubt" className="bg-[#0F1B3D]">Doubt Chat</option>
              <option value="test" className="bg-[#0F1B3D]">Test</option>
              <option value="homework" className="bg-[#0F1B3D]">Homework Solver</option>
              <option value="other" className="bg-[#0F1B3D]">Other</option>
            </select>
            
            <label className="text-[11px] text-white/50 font-semibold uppercase mb-1 block">Describe the error</label>
            <textarea
              data-testid="global-report-textarea"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What went wrong? E.g., 'The answer for question 3 was incorrect...' or 'Page not loading properly...'"
              className="w-full h-24 rounded-lg bg-white/5 border border-white/10 text-white text-sm p-3 resize-none outline-none focus:border-orange-500 placeholder:text-white/20"
            />
            <button data-testid="global-report-submit" onClick={submit} disabled={sending}
              className="w-full mt-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm h-10 flex items-center justify-center gap-2 transition-all disabled:opacity-50">
              {sending ? "Sending..." : <><Send className="w-4 h-4" /> Submit Report</>}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
